import { useState } from "react";
import { Badge, Button } from "../base";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import { contactService } from "../../services/api";

export function ContactSection({ id, ...rest }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFeedback(null);
        setSending(true);
        try {
            const res = await contactService.submit({ name, email, message });
            if (res.data?.success) {
                setFeedback({ type: "ok", text: res.data.message || "Pesan berhasil dikirim." });
                setName("");
                setEmail("");
                setMessage("");
            }
        } catch (err) {
            const msg =
                err.response?.data?.errors &&
                Object.values(err.response.data.errors).flat().join(" ");
            setFeedback({
                type: "err",
                text: msg || err.response?.data?.message || "Gagal mengirim pesan. Coba lagi nanti.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id={id}
            className="w-full py-24 bg-white dark:bg-base-950"
            {...rest}
        >
            <div className="container max-w-6xl px-4 sm:px-6 lg:px-8 mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="flex flex-col items-center text-center gap-4 max-w-3xl mx-auto mb-12"
                >
                    <Badge label="Kontak" leading />
                    <h2 className="text-4xl lg:text-5xl font-display text-title dark:text-white font-medium leading-tight">
                        Hubungi Kami
                    </h2>
                    <p className="text-muted dark:text-base-400 text-base font-body max-w-2xl">
                        Ada pertanyaan atau ingin mulai? Kami siap membantu klinik kamu beralih ke sistem antrian yang
                        lebih modern.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                    className="w-full max-w-3xl mx-auto"
                >
                    {feedback && (
                        <div
                            className={`mb-6 rounded-xl px-4 py-3 text-sm font-medium text-center ${
                                feedback.type === "ok"
                                    ? "bg-emerald-50 text-emerald-800 border border-emerald-100"
                                    : "bg-red-50 text-red-800 border border-red-100"
                            }`}
                        >
                            {feedback.text}
                        </div>
                    )}
                    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                className="flex flex-col gap-2"
                            >
                                <label
                                    htmlFor="name"
                                    className="text-title dark:text-base-200 font-medium text-sm"
                                >
                                    Nama
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted dark:text-base-500 pointer-events-none">
                                        <Icon icon="octicon:person-24" width="20" height="20" />
                                    </span>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Masukkan nama Anda"
                                        required
                                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-base-300 dark:border-base-700 bg-white dark:bg-base-900 text-title dark:text-base-100 focus:outline-none focus:ring-2 focus:ring-cyan-200 transition-all placeholder:text-base-300 dark:placeholder:text-base-600"
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.35 }}
                                className="flex flex-col gap-2"
                            >
                                <label
                                    htmlFor="email"
                                    className="text-title dark:text-base-200 font-medium text-sm"
                                >
                                    Email
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted dark:text-base-500 pointer-events-none">
                                        <Icon icon="clarity:email-line" width="20" height="20" />
                                    </span>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Masukkan email Anda"
                                        required
                                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-base-300 dark:border-base-700 bg-white dark:bg-base-900 text-title dark:text-base-100 focus:outline-none focus:ring-2 focus:ring-cyan-200 transition-all placeholder:text-base-300 dark:placeholder:text-base-600"
                                    />
                                </div>
                            </motion.div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            className="flex flex-col gap-2"
                        >
                            <label
                                htmlFor="message"
                                className="text-title dark:text-base-200 font-medium text-sm"
                            >
                                Pesan
                            </label>
                            <textarea
                                id="message"
                                rows={6}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Masukkan pesan Anda"
                                required
                                className="w-full px-4 py-3 rounded-xl border border-base-300 dark:border-base-700 bg-white dark:bg-base-900 text-title dark:text-base-100 focus:outline-none focus:ring-2 focus:ring-cyan-200 transition-all placeholder:text-base-300 dark:placeholder:text-base-600 resize-none"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="mt-2"
                        >
                            <Button
                                label={sending ? "Mengirim…" : "Kirim Pesan"}
                                icon="tabler:arrow-right"
                                color="dark"
                                type="submit"
                                className="px-8"
                            />
                        </motion.div>
                    </form>
                </motion.div>
            </div>
        </section>
    );
}
